// src/pages/Terms.tsx
//
// Terms of Use. Content sourced from the canonical document
// (Astra Tech, LLC operating as Instant Tours).
//
// IMPORTANT: the canonical doc states a minimum age of 13 in section
// 4.1. We override that to 16 here so the document is consistent
// with the Privacy Policy (which states 16) and with the actual
// SignUpForm enforcement (which already gates at 16). This avoids
// the user-facing inconsistency of "Privacy says 16 but Terms says
// 13" and aligns with EEA/UK GDPR thresholds we operate under.
//
// Version is stamped on user consent records (gdprConsent
// .privacyPolicyVersion in authStore). Bump on substantive changes.

import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Alert,
  Link,
} from '@mui/material';

const UPDATED = '2026-04-30';
const VERSION = '3.0';

const Bullets: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <Box
    component="ul"
    sx={{
      pl: 3,
      my: 1,
      '& li': { mb: 0.5, lineHeight: 1.6 },
    }}
  >
    {items.map((it, i) => (
      <li key={i}>
        <Typography component="span" variant="body2">
          {it}
        </Typography>
      </li>
    ))}
  </Box>
);

const Section: React.FC<{ n: string; title: string; children: React.ReactNode }> = ({
  n,
  title,
  children,
}) => (
  <Box sx={{ mt: 4 }}>
    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1.5 }}>
      {n}. {title}
    </Typography>
    {children}
  </Box>
);

const Sub: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Box sx={{ mt: 2.5 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.75 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.65 }}>
    {children}
  </Typography>
);

export const Terms: React.FC = () => {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={1} sx={{ p: { xs: 3, md: 4 } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
          Terms of Use
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Effective {UPDATED} · Version {VERSION}
        </Typography>

        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            For general information and entertainment only
          </Typography>
          <Typography variant="body2">
            Instant Tours uses artificial intelligence that can produce
            inaccurate, misleading, or fabricated information. The Service
            is not a navigation system, safety guidance tool, or
            professional advice of any kind. You are solely responsible
            for verifying information and for all decisions and actions
            you take based on your use of the Service. You use the Service
            at your own risk.
          </Typography>
        </Alert>

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Welcome to Instant Tours
          </Typography>
          <P>
            These Terms of Use, or “Terms,” govern your access to and use
            of Instant Tours, including our websites, mobile applications,
            products, features, content, services, technologies, and
            software, except where we expressly state that separate terms
            apply.
          </P>
          <P>
            Instant Tours is operated by Astra Tech, LLC, doing business
            as Instant Tours. These Terms form an agreement between you
            and Astra Tech, LLC. If you do not agree to these Terms, you
            should not access or use Instant Tours.
          </P>
        </Box>

        <Section n="1" title="The Instant Tours Service">
          <P>
            We agree to provide you with the Instant Tours service, or the
            “Service.” The Service includes the products, features,
            applications, services, technologies, and software that we
            provide to help people discover, create, share, and experience
            tours, places, routes, recommendations, and location-based
            content.
          </P>

          <Sub title="Offering personalized ways to discover, create, connect, and explore">
            <P>
              People travel, explore, and experience places differently.
              Instant Tours helps you discover tours, destinations, points
              of interest, local experiences, recommendations, and content
              that may be relevant to you. We may offer different types of
              accounts, tools, and features that allow you to browse, save,
              create, share, or interact with tours and travel-related
              content. We may use information about your activity,
              preferences, location, interactions, and other information
              described in our Privacy Policy to personalize the Service.
            </P>
          </Sub>

          <Sub title="Fostering a positive, trustworthy, and safe environment">
            <P>
              We may develop and use tools, systems, and processes to
              detect, prevent, and respond to misuse, fraud, abuse, harmful
              behavior, misleading content, unsafe activity, violations of
              these Terms, or violations of our policies. Where appropriate,
              we may share information with service providers, legal
              authorities, or other parties when permitted or required by
              law.
            </P>
          </Sub>

          <Sub title="Developing and using technologies that help us provide and improve the Service">
            <P>
              We use technology to operate, analyze, personalize, protect,
              and improve Instant Tours. This may include automated systems,
              artificial intelligence, machine learning, data analysis,
              recommendation systems, mapping tools, location-based
              technologies, and other technical processes that help us
              deliver tours, recommendations, safety features, search
              results, content organization, advertising, and service
              improvements.
            </P>
          </Sub>

          <Sub title="Providing location-based and travel-related experiences">
            <P>
              Instant Tours may use location information, map data, user
              activity, creator content, business information, and
              third-party data to help users discover nearby tours, places,
              experiences, routes, and recommendations. Location-based
              features may depend on the permissions and settings you
              choose on your device or within the Service.
            </P>
            <P>
              Information shown through the Service may come from us, other
              users, creators, businesses, third-party providers, or public
              sources. We work to provide useful and relevant information,
              but places, conditions, availability, access rules, pricing,
              hours, safety conditions, and other real-world details may
              change.
            </P>
          </Sub>

          <Sub title="Ensuring access to our Service">
            <P>
              To operate and provide the Service, we may store, process,
              and transfer information using systems, service providers,
              and infrastructure located in the United States and other
              countries. This may include processing information outside
              your country of residence. More information about how we
              collect, use, store, transfer, and protect information is
              available in our Privacy Policy.
            </P>
          </Sub>

          <Sub title="Connecting you with brands, products, services, and experiences">
            <P>
              Instant Tours may be supported by advertising, sponsored
              content, partnerships, promotions, affiliate relationships,
              paid placements, subscriptions, or other commercial features.
              We may use information from your use of the Service, and
              information from partners or third-party providers, to show
              you ads, offers, sponsored content, businesses, experiences,
              or recommendations that may be relevant to you. We do not
              sell your personal information in the ordinary meaning of
              the word.
            </P>
          </Sub>

          <Sub title="Research and innovation">
            <P>
              We may use information we have to study, maintain, improve,
              and develop the Service, including to improve recommendations,
              search, maps, safety systems, advertising, content quality,
              accessibility, user experience, and future products or
              features. We may also use aggregated or de-identified
              information for research, analytics, and service improvement.
            </P>
          </Sub>
        </Section>

        <Section n="2" title="How our Service is funded">
          <P>
            Instead of paying to use Instant Tours, you acknowledge that
            we may support and operate the Service through advertising,
            sponsored content, partnerships, and other commercial
            relationships. We may show you ads, offers, sponsored content,
            featured tours, promoted listings, or recommendations from
            businesses, creators, and organizations. These may appear
            within the Service or alongside content you view.
          </P>
          <P>
            To make these experiences more relevant, we may use information
            about your activity, interactions, preferences, approximate
            location, and use of the Service, as described in our Privacy
            Policy. We aim to show relevant and useful advertising without
            directly identifying you to advertisers. We do not share
            information that directly identifies you, such as your name or
            email address, with advertisers unless you give us explicit
            permission.
          </P>
          <P>
            We may provide advertisers, partners, or creators with
            aggregated or anonymized reporting about the performance of
            their content, such as general engagement trends or audience
            insights. These reports do not include information that
            directly identifies you.
          </P>
          <P>
            You may also see content created or shared by users, creators,
            or partners that includes commercial relationships,
            sponsorships, or promotions. We may provide tools or
            requirements for labeling or identifying such content where
            applicable. As Instant Tours evolves, we may introduce
            additional paid features, subscriptions, premium services, or
            commercial offerings.
          </P>
        </Section>

        <Section n="3" title="The Privacy Policy">
          <P>
            Providing our Service requires collecting and using your
            information. Our Privacy Policy explains how we collect, use,
            store, process, and share your information when you use Instant
            Tours, and the choices and controls available to you. By using
            the Service, you acknowledge that your information will be
            handled as described in our Privacy Policy.
          </P>
          <P>
            Where required by applicable law, including the General Data
            Protection Regulation, we rely on specific legal bases to
            process your information, such as your consent, the performance
            of a contract with you, or our legitimate interests. If you do
            not agree with how we collect, use, and process your
            information as described in the Privacy Policy, you should not
            use the Service.
          </P>
        </Section>

        <Section n="4" title="Your commitments">
          <P>
            In return for our commitment to provide the Service, you agree
            to the following.
          </P>

          <Sub title="4.1 Who can use Instant Tours">
            <P>
              We want Instant Tours to be accessible, safe, and compliant
              with applicable laws. By using the Service, you confirm that:
            </P>
            <Bullets
              items={[
                <>
                  <strong>You are at least 16 years old.</strong> Instant
                  Tours is not available to individuals under the age of 16.
                </>,
                'You are not prohibited from using the Service under applicable laws or regulations',
                'We have not previously disabled your account for violations of law or these Terms',
                'You will use the Service in compliance with all applicable laws',
              ]}
            />
          </Sub>

          <Sub title="4.2 How you can’t use Instant Tours">
            <P>You agree that you will not:</P>
            <Bullets
              items={[
                'Provide false, misleading, or inaccurate information, or impersonate any person or entity',
                'Use the Service for any unlawful, fraudulent, or unauthorized purpose',
                'Violate these Terms or any policies we provide in connection with the Service',
                'Interfere with, disrupt, or impair the operation or security of the Service',
                'Attempt to access accounts, systems, or data without authorization',
                'Use automated means to access, scrape, or collect data from the Service without our permission',
                'Buy, sell, transfer, or exploit accounts, data, or access credentials from the Service',
                'Post or share content that is unlawful, misleading, defamatory, infringing, or otherwise violates the rights of others, including intellectual property rights',
                'Upload or share content that you do not have the rights or permissions to use',
                'Reverse engineer, modify, or attempt to extract the source code of the Service',
                'Circumvent or attempt to bypass any security or access controls',
              ]}
            />

            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mt: 2, mb: 0.5 }}
            >
              Travel and location-specific use
            </Typography>
            <P>
              Because Instant Tours may relate to real-world locations and
              experiences, you also agree that you will not:
            </P>
            <Bullets
              items={[
                'Use the Service in a way that endangers yourself or others',
                'Rely on the Service for emergency, safety-critical, or real-time navigation decisions',
                'Misrepresent locations, access conditions, or safety information in content you create or share',
                'Use the Service in violation of local laws, access restrictions, or property rights',
              ]}
            />
          </Sub>

          <Sub title="4.3 Permissions you give to us">
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mt: 1, mb: 0.5 }}
            >
              Content license
            </Typography>
            <P>
              You retain ownership of the content you create and share on
              Instant Tours. However, when you upload, post, or share
              content through the Service, you grant Astra Tech, LLC a
              non-exclusive, royalty-free, transferable, sublicensable,
              worldwide license to host, use, reproduce, distribute,
              modify, display, and perform that content as necessary to
              operate, provide, improve, and promote the Service.
            </P>
            <P>This license allows us to:</P>
            <Bullets
              items={[
                'Display your content to other users',
                'Adapt content for different formats, devices, or features',
                'Include content in recommendations, search results, or promotional materials related to the Service',
              ]}
            />
            <P>
              This license ends when your content is deleted from our
              systems, except where it has been shared with others or
              retained for legal, operational, or security purposes.
            </P>

            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mt: 2, mb: 0.5 }}
            >
              Responsibility for your content
            </Typography>
            <P>You represent and warrant that:</P>
            <Bullets
              items={[
                'You own or have obtained all necessary rights to the content you share',
                'Your content does not violate any laws or the rights of any third party',
              ]}
            />

            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mt: 2, mb: 0.5 }}
            >
              Updates to the Service
            </Typography>
            <P>
              You agree that we may update, modify, or improve the Service
              and may automatically download and install updates where
              applicable.
            </P>
          </Sub>
        </Section>

        <Section n="5" title="Additional rights we retain">
          <Bullets
            items={[
              <>
                <strong>Usernames and identifiers.</strong> If you select a
                username or similar identifier, we may change it if we
                believe it is appropriate or necessary, including where it
                infringes on another party’s rights, impersonates someone
                else, or violates these Terms.
              </>,
              <>
                <strong>Our content and intellectual property.</strong> If
                you use content, features, or materials we make available
                as part of the Service — including designs, layouts, maps,
                data, software, or other materials — we retain all rights
                to that content and intellectual property.
              </>,
              <>
                <strong>Limited use of our materials.</strong> You may only
                use our intellectual property, trademarks, branding, or
                materials as expressly permitted by us or with our prior
                written consent.
              </>,
              <>
                <strong>Restrictions on modification and extraction.</strong>{' '}
                You may not copy, modify, distribute, sell, lease, reverse
                engineer, decompile, or otherwise attempt to extract source
                code or underlying components of the Service, except where
                permitted by applicable law or an open source license.
              </>,
              <>
                <strong>Service changes.</strong> We may modify, suspend,
                or discontinue any part of the Service at any time, where
                reasonably necessary to operate, improve, or comply with
                legal or regulatory requirements.
              </>,
            ]}
          />
        </Section>

        <Section n="6" title="Content removal and disabling or terminating your account">
          <Sub title="Content removal">
            <P>
              We may remove, limit, or disable access to any content you
              share through the Service if we believe it violates these
              Terms, our policies, applicable laws, or the rights of
              others, or if we determine it may be misleading, harmful,
              unsafe, or otherwise inappropriate.
            </P>
          </Sub>
          <Sub title="Account suspension or termination">
            <P>
              We may suspend, restrict, or terminate your access to all or
              part of the Service at any time if you violate these Terms
              or applicable laws, create risk, harm, or legal exposure for
              us, other users, or third parties, where required by law or
              regulatory authority, or where reasonably necessary to
              protect the integrity, security, or operation of the Service.
            </P>
          </Sub>
          <Sub title="User-initiated deletion">
            <P>
              You may delete your content or your account at any time using
              the tools provided within the Service, where available.
            </P>
          </Sub>
          <Sub title="Deletion process and retention">
            <P>
              When you request deletion, we will begin the deletion process
              within a reasonable timeframe. Content may remain in backup
              systems, logs, or archives for a limited period where
              necessary for technical, legal, or security purposes. We may
              retain content for longer where necessary to comply with
              legal obligations, resolve disputes or enforce these Terms,
              detect, prevent, or investigate misuse, fraud, or illegal
              activity, or protect the safety and security of users,
              systems, or the public.
            </P>
          </Sub>
          <Sub title="Effect of termination">
            <P>
              If your account is terminated or deleted, these Terms will
              no longer apply as an active agreement between you and us,
              except for provisions that by their nature should survive
              termination — including those relating to content rights,
              limitations of liability, and dispute resolution.
            </P>
          </Sub>
        </Section>

        <Section n="7" title="Our agreement and what happens if we disagree">
          <Sub title="7.1 Our agreement">
            <P>
              These Terms constitute the entire agreement between you and
              Astra Tech, LLC, doing business as Instant Tours, regarding
              your use of the Service. If you use additional features or
              services, we may provide supplemental terms, policies, or
              guidelines that also apply; if there is a conflict, the
              additional terms will govern for that specific feature or
              service. If any provision is found to be invalid or
              unenforceable, the remaining provisions will remain in full
              force and effect. Our failure to enforce any provision is
              not a waiver of our rights. We reserve all rights not
              expressly granted to you.
            </P>
          </Sub>

          <Sub title="7.2 Who has rights under this agreement">
            <P>
              These Terms do not create any rights for third parties. You
              may not transfer or assign your rights or obligations under
              these Terms without our prior written consent. We may assign
              or transfer our rights and obligations under these Terms,
              including in connection with a merger, acquisition, sale of
              assets, or by operation of law.
            </P>
          </Sub>

          <Sub title="7.3 Disclaimer of warranties">
            <P>
              <strong>
                Instant Tours is provided for general informational and
                entertainment purposes only. The Service is not a
                navigation system, safety guidance tool, or professional
                advice of any kind.
              </strong>
            </P>
            <P>
              We do not guarantee that tours, routes, locations, directions,
              recommendations, or other content are accurate, complete,
              reliable, current, or suitable for your specific situation.
              Real-world conditions, including but not limited to access,
              terrain, traffic, weather, hours of operation, pricing,
              safety conditions, and legal restrictions, may change at any
              time.
            </P>
            <P>
              You are solely responsible for verifying information and for
              all decisions and actions you take based on your use of the
              Service. You agree that you use the Service at your own
              risk. To the fullest extent permitted by law, Astra Tech,
              LLC is not responsible for any injury, loss, damage, or harm
              resulting from your use of or reliance on the Service,
              including in connection with travel, navigation, access to
              locations, or participation in any activities or experiences.
            </P>
          </Sub>

          <Sub title="7.4 Limitation of liability">
            <P>
              To the fullest extent permitted by law, Astra Tech, LLC and
              its affiliates, officers, employees, and partners will not
              be liable for any indirect, incidental, consequential,
              special, exemplary, or punitive damages, or for any loss of
              profits, revenue, data, or goodwill, arising out of or
              related to your use of the Service.
            </P>
            <P>We are not responsible for:</P>
            <Bullets
              items={[
                'The actions, content, or conduct of other users, creators, businesses, or third parties',
                'Any third-party services, locations, or experiences accessed through the Service',
                'Any decisions you make based on information provided through the Service',
              ]}
            />
            <P>
              You acknowledge that use of the Service may involve
              real-world activities and locations, and you assume all
              risks associated with your use of the Service. Where
              liability cannot be excluded under applicable law, our
              liability will be limited to the maximum extent permitted by
              law.
            </P>
          </Sub>

          <Sub title="7.5 Dispute resolution and governing law">
            <P>
              If you are a consumer, you may have rights under the laws of
              your country of residence. These Terms, and any dispute
              arising out of or relating to them, will be governed by the
              laws of England and Wales, unless otherwise required by
              applicable law. You agree that disputes may be resolved in
              the courts of England and Wales, unless applicable law
              provides otherwise. Nothing in these Terms limits any rights
              you may have under applicable consumer protection laws.
            </P>
          </Sub>

          <Sub title="7.6 Feedback and suggestions">
            <P>
              We welcome feedback, ideas, and suggestions. By submitting
              feedback to us, you agree that we may use it without
              restriction or obligation to compensate you, and without any
              obligation to keep it confidential.
            </P>
          </Sub>
        </Section>

        <Section n="8" title="Updating these Terms">
          <P>
            We may update these Terms from time to time to reflect changes
            to the Service, our business practices, legal requirements, or
            other operational needs. Where we make material changes, we
            will provide notice — through the Service, by email, or through
            other reasonable means — and give you an opportunity to review
            the updated Terms before they take effect, where required by
            applicable law. By continuing to access or use the Service
            after the updated Terms become effective, you agree to be
            bound by the revised Terms. If you do not agree, you must stop
            using the Service and may delete your account, where
            applicable.
          </P>
        </Section>

        <Section n="9" title="Contact">
          <P>
            Astra Tech, LLC<br />
            Instant Tours (DBA)<br />
            <Link href="mailto:hello@theastralabs.com">
              hello@theastralabs.com
            </Link>
          </P>
        </Section>
      </Paper>
    </Container>
  );
};
